import type { ProvenanceSource } from './provenance-badge';

/**
 * From docs/next-steps.md.
 *
 * Shared helper that normalizes a server-side provenance hint into the
 * canonical `ProvenanceSource` union the badge component renders.
 * Views must derive provenance from API response metadata; static
 * `'simulator'` defaults are forbidden by the release-blocker audit.
 *
 * Resolution order:
 *   1. The response's `__source` field — accepted verbatim if it
 *      matches the union; otherwise normalized as best as possible.
 *   2. If the response is missing a hint and the row count is 0 →
 *      `'empty'`.
 *   3. Otherwise → `'unknown'` (never `'simulator'`).
 */
export type ResponseEnvelope = {
  __source?: string | null;
  __freshAt?: string | null;
};

export interface NormalizeProvenanceArgs {
  /** Raw response (or response envelope) returned from the API. */
  response?: ResponseEnvelope | null;
  /** Number of rows in the response, used to disambiguate empty vs unknown. */
  rowCount?: number;
}

const KNOWN: ReadonlyArray<ProvenanceSource> = [
  'live',
  'simulator',
  'seed',
  'imported',
  'manual',
  'replay',
  'unknown',
  'empty',
  'not-implemented',
  'not-connected',
];

export function normalizeProvenance(args: NormalizeProvenanceArgs): ProvenanceSource {
  const raw = args.response?.__source?.toLowerCase().trim();
  if (raw && (KNOWN as ReadonlyArray<string>).includes(raw)) {
    return raw as ProvenanceSource;
  }
  const rowCount = args.rowCount ?? 0;
  if (rowCount === 0) return 'empty';
  return 'unknown';
}

/**
 * Convenience hook-friendly wrapper for the canonical "envelope-or-list"
 * pattern many views use. The arg may be:
 *   - an array (rows; provenance is `'unknown'` if non-empty, `'empty'` if zero)
 *   - an envelope `{ __source, data: T[] }` (provenance comes from `__source`)
 *   - undefined / null (provenance is `'empty'`)
 */
export function provenanceOf<T>(input: T[] | (ResponseEnvelope & { data?: T[] }) | undefined | null): ProvenanceSource {
  if (input == null) return 'empty';
  if (Array.isArray(input)) {
    return input.length === 0 ? 'empty' : 'unknown';
  }
  return normalizeProvenance({ response: input, rowCount: input.data?.length ?? 0 });
}
